/**
 * Stock Service — API-ready CRUD for medication inventory management.
 *
 * Persistence is localStorage; swap method bodies for fetch() when a backend
 * is available.  Return contract: { success, data?, error? }.
 */

import LocalStorageService from '../storage/LocalStorageService';
import { createAuditEntry } from '../../utils/auditLogger';

const STORE_KEY = 'medication_stock_v1';
const LOG_PREFIX = '[StockService]';

const SEED_STOCK = [
  { id: 'STK-001', name: 'Paracetamol 500mg', genericName: 'Paracetamol', category: 'Analgesic', quantity: 500, minQuantity: 100, unit: 'tablets', supplier: 'PharmaCorp', batchNumber: 'PC-2024-001', expiryDate: '2026-12-31', location: 'Med Room A', lastOrdered: '2024-12-01', unitCost: 0.12 },
  { id: 'STK-002', name: 'Metformin 500mg', genericName: 'Metformin', category: 'Antidiabetic', quantity: 300, minQuantity: 50, unit: 'tablets', supplier: 'DiabetesCare', batchNumber: 'DC-2024-042', expiryDate: '2026-06-30', location: 'Med Room A', lastOrdered: '2024-11-15', unitCost: 0.18 },
  { id: 'STK-003', name: 'Warfarin 5mg', genericName: 'Warfarin', category: 'Anticoagulant', quantity: 40, minQuantity: 50, unit: 'tablets', supplier: 'BloodCare', batchNumber: 'BC-2024-015', expiryDate: '2025-09-30', location: 'S8 Cabinet', lastOrdered: '2024-10-20', unitCost: 0.35 },
  { id: 'STK-004', name: 'Insulin Glargine', genericName: 'Insulin', category: 'Antidiabetic', quantity: 15, minQuantity: 10, unit: 'pens', supplier: 'InsulinPro', batchNumber: 'IP-2024-088', expiryDate: '2025-08-31', location: 'Fridge A', lastOrdered: '2024-12-10', unitCost: 42.5 },
  { id: 'STK-005', name: 'Morphine 10mg', genericName: 'Morphine', category: 'S8 Opioid', quantity: 20, minQuantity: 15, unit: 'ampoules', supplier: 'PainRelief Co', batchNumber: 'PR-2024-003', expiryDate: '2026-03-31', location: 'S8 Cabinet', lastOrdered: '2024-11-28', unitCost: 8.75 },
  { id: 'STK-006', name: 'Omeprazole 20mg', genericName: 'Omeprazole', category: 'PPI', quantity: 250, minQuantity: 60, unit: 'capsules', supplier: 'GastroCare', batchNumber: 'GC-2024-120', expiryDate: '2026-09-30', location: 'Med Room B', lastOrdered: '2024-12-05', unitCost: 0.22 },
];

function _audit(action, entityId, oldValue, newValue, userId) {
  createAuditEntry({
    eventType: 'data_modification',
    entityType: 'medication_stock',
    entityId,
    action,
    oldValue,
    newValue,
    userId,
  });
}

function _ensureSeeded() {
  let data = LocalStorageService.get(STORE_KEY, null);
  if (!data) {
    const now = new Date().toISOString();
    data = SEED_STOCK.map((s) => ({ ...s, createdAt: now, updatedAt: now }));
    LocalStorageService.set(STORE_KEY, data);
  }
  return data;
}

class StockService {
  static async getAll() {
    return { success: true, data: _ensureSeeded() };
  }

  static async getById(id) {
    const all = _ensureSeeded();
    const found = all.find((s) => s.id === id);
    if (!found) return { success: false, error: 'Stock item not found' };
    return { success: true, data: found };
  }

  static async addStock(stockData, userId = 'system') {
    if (!stockData.name) return { success: false, error: 'Name is required' };
    const all = _ensureSeeded();
    const now = new Date().toISOString();
    const item = {
      id: `STK-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      ...stockData,
      quantity: parseInt(stockData.quantity, 10) || 0,
      minQuantity: parseInt(stockData.minQuantity, 10) || 0,
      createdAt: now,
      updatedAt: now,
    };
    all.unshift(item);
    LocalStorageService.set(STORE_KEY, all);
    _audit('create', item.id, null, item, userId);
    console.log(LOG_PREFIX, 'Added stock', item.id, item.name);
    return { success: true, data: item };
  }

  static async updateStock(id, updates, userId = 'system') {
    const all = _ensureSeeded();
    const idx = all.findIndex((s) => s.id === id);
    if (idx === -1) return { success: false, error: 'Stock item not found' };

    const prev = { ...all[idx] };
    all[idx] = { ...all[idx], ...updates, id, updatedAt: new Date().toISOString() };
    LocalStorageService.set(STORE_KEY, all);
    _audit('update', id, prev, all[idx], userId);
    console.log(LOG_PREFIX, 'Updated stock', id);
    return { success: true, data: all[idx] };
  }

  static async adjustQuantity(id, delta, reason, userId = 'system') {
    const all = _ensureSeeded();
    const idx = all.findIndex((s) => s.id === id);
    if (idx === -1) return { success: false, error: 'Stock item not found' };

    const prev = all[idx].quantity;
    const newQty = prev + delta;
    if (newQty < 0) return { success: false, error: 'Quantity cannot go below zero' };

    all[idx].quantity = newQty;
    all[idx].updatedAt = new Date().toISOString();
    LocalStorageService.set(STORE_KEY, all);
    _audit('update', id, { quantity: prev }, { quantity: newQty, reason }, userId);
    console.log(LOG_PREFIX, 'Adjusted stock', id, prev, '->', newQty, reason);
    return { success: true, data: all[idx] };
  }

  static async deleteStock(id, userId = 'system') {
    const all = _ensureSeeded();
    const idx = all.findIndex((s) => s.id === id);
    if (idx === -1) return { success: false, error: 'Stock item not found' };

    const removed = all.splice(idx, 1)[0];
    LocalStorageService.set(STORE_KEY, all);
    _audit('delete', id, removed, null, userId);
    console.log(LOG_PREFIX, 'Deleted stock', id);
    return { success: true, data: removed };
  }

  static async getLowStockAlerts() {
    const all = _ensureSeeded();
    const alerts = all.filter((s) => s.quantity <= s.minQuantity);
    return { success: true, data: alerts };
  }
}

export default StockService;
